import { Icon } from '../ui/Icon'
import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { explainPushError, pushRoutesMissing } from './explainPushError'
import {
  enablePushOnThisDevice,
  pushPrefs,
  pushSupported,
  setPushPref,
} from './pushOptIn'

/**
 * "Notify me when this agent has something" — one switch, in the sheet behind an
 * agent's face.
 *
 * Owner, 2026-09-18: 「可以在Agent的設定頁面,也就是按下Agent它的頭像的時候彈跳出來
 * 的那個設定頁面,可以設定說我需要這個Agent做push notification,或是不要…可以只讓少數
 * 的Agent回報push notification,而不會淹沒使用者的整個螢幕」
 *
 * OFF BY DEFAULT AND ABSENT WHERE IT CANNOT WORK. The 1.0 store build ships no
 * push plugin, so there the switch is not rendered at all rather than rendered
 * broken — a control that invites a tap and then explains nothing is worse than
 * no control.
 *
 * TWO DIFFERENT "OFF"s, kept apart because they need different actions from the
 * person. The account preference is ours and the switch owns it. iOS permission
 * is the system's, it can only be granted once, and after a refusal nothing in
 * this app can re-ask — so that state says where to go instead of failing
 * silently, which is the one outcome that would make the feature look broken
 * while working exactly as designed.
 */
export function PushToggle({
  deviceId,
  channelHash,
}: {
  deviceId: string
  channelHash: string
}) {
  const qc = useQueryClient()
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)

  const prefs = useQuery({
    queryKey: ['push-prefs'],
    queryFn: pushPrefs,
    // Only meaningful inside the shell; a browser never renders this.
    enabled: pushSupported(),
  })

  if (!pushSupported()) return null
  /*
   * A SERVER WITHOUT THESE ROUTES SHOWS NO SWITCH, rather than a switch that
   * explains itself. Owner, 2026-09-18: he ticked this on app-dev while the
   * BFF image was still building and was told notifications were unavailable.
   * The sentence was true and the control should not have been there to read
   * it. Costs nothing — the read already ran when the sheet opened.
   */
  if (pushRoutesMissing(prefs.error)) return null

  const on = (prefs.data ?? []).some(
    (p) => p.device_id === deviceId && p.channel_hash === channelHash,
  )

  const toggle = async () => {
    if (busy) return
    setBusy(true)
    setNote(null)
    try {
      if (!on) {
        /*
         * PERMISSION FIRST, AND ONLY WHEN SWITCHING ON. Asking on mount would
         * spend the single system prompt on someone who never wanted
         * notifications — iOS gives one chance, and a "Don't Allow" collected
         * before the person expressed any interest cannot be taken back from
         * inside the app.
         */
        const perm = await enablePushOnThisDevice()
        if (perm === 'denied') {
          setNote(
            'Notifications are off for Avengents in iOS Settings — turn them on there first.',
          )
          return
        }
        if (perm !== 'granted') {
          setNote('iOS did not grant notifications. Try again.')
          return
        }
      }
      await setPushPref(deviceId, channelHash, !on)
      await qc.invalidateQueries({ queryKey: ['push-prefs'] })
    } catch (e) {
      setNote(explainPushError(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      {/* One row of the agent card's list (owner, 2026-09-30: 簡單簡潔): icon, label, switch. */}
      <label className="ac-row ac-row-switch">
        <Icon name="notifications" size={18} />
        <span className="ac-row-label">Notify when done</span>
        <input
          type="checkbox"
          className="ac-switch"
          checked={on}
          disabled={busy || prefs.isLoading}
          onChange={() => void toggle()}
          aria-label="Notify me when this agent finishes"
        />
      </label>
      {/* The read fails the same way on a server without these routes, so say it
          on arrival rather than letting the person tap to find out. */}
      {(note ?? (prefs.error ? explainPushError(prefs.error) : null)) && (
        <p className="err-line sm-err">
          {note ?? explainPushError(prefs.error)}
        </p>
      )}
    </>
  )
}
